import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Feather, Loader2, Check } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export default function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-cream px-6">
      <form onSubmit={onSubmit} className="w-full max-w-md bg-white rounded-2xl border border-app-border p-6 sm:p-8 space-y-5" data-testid="forgot-password-form">
        <div className="flex items-center gap-2 text-olive">
          <Feather className="w-5 h-5" />
          <span className="text-xs font-semibold uppercase tracking-[0.3em]">Aviary Studio</span>
        </div>
        <h1 className="font-heading text-3xl font-semibold text-espresso">
          {t("auth.forgot_password")}
        </h1>
        <p className="text-sm text-espresso/70">{t("auth.forgot_sub")}</p>

        {sent ? (
          <div className="bg-olive/10 border border-olive/30 text-olive text-sm rounded-lg p-3 flex items-start gap-2" data-testid="forgot-sent-message">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>{t("auth.forgot_sent")}</div>
          </div>
        ) : (
          <>
            <div>
              <Label htmlFor="email">{t("common.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@aviary.com"
                className="mt-1.5"
                data-testid="forgot-email-input"
              />
            </div>
            {err && (
              <div className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-olive hover:bg-olive-hover text-white h-11 rounded-lg"
              data-testid="forgot-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.forgot_send")}
            </Button>
          </>
        )}

        <p className="text-sm text-center text-espresso/70">
          <Link to="/login" className="text-olive font-semibold hover:underline" data-testid="back-to-login-link">
            ← {t("common.signin")}
          </Link>
        </p>
      </form>
    </div>
  );
}
